// The shared GLSL prelude of every world-quad fragment shader, and the
// typed uniform list the passes are configured from.
//
// The three world passes — territory, terrain and heatmap — each draw ONE
// full-world quad. Every one of them needs the same things: the hex
// topology of `../../world.ts`, the per-cell data textures, the camera
// figures and the premultiplied output. That shared half lives here and is
// concatenated in front of each pass source (plan Part B, §B.2).
//
// Two rules govern this directory:
//
//  1. **No figure is a GLSL literal.** Every colour, width, alpha, ring
//     table, feather figure, tick figure and heat ramp reaches the shader as
//     a uniform whose default comes from `gen/presentation.ts` or
//     `gen/balance.ts` (the data layer, CLAUDE.md). A literal in the GLSL is
//     a value that a designer cannot patch.
//  2. **No `fwidth` / `dFdx` / `dFdy`.** Every field this directory builds
//     has an analytic gradient, and a screen-space derivative glitches
//     exactly on the crease where the 0.95 border line sits. Coverage comes
//     from `lineCov` and from `clamp(sd/px + 0.5, 0, 1)`.

import { COL_PITCH, FACTIONS, HEX_SIZE } from '../../gen/balance';
import { ACCENT_COLOR, NEUTRAL_COLOR, TERRAIN_RADIUS_K } from '../../gen/presentation';
import { rgb } from '../../render/color';
import { IDENTITY_GLSL } from './identity';

/** The GLSL type of a uniform, as the pass setter must write it. */
export type UniformType =
  | 'float' | 'int' | 'vec2' | 'vec3' | 'vec4' | 'ivec2' | 'mat3'
  | 'sampler' | 'float[]' | 'vec3[]' | 'vec4[]';

/**
 * One uniform a pass declares, with the default the TS side must set.
 *
 * `unit` is the texture unit a `sampler` expects, fixed by the A.6 contract.
 * `dynamic` marks a uniform the engine's `WorldPass` writes every frame from
 * the camera or the clock: its `value` here is only a safe starting figure,
 * never the truth.
 */
export interface UniformSpec {
  readonly name: string;
  readonly type: UniformType;
  readonly value: number | readonly number[];
  readonly unit?: number;
  readonly dynamic?: boolean;
  readonly note?: string;
  /**
   * What ONE texel of a `sampler` holds. The default is `f32`.
   *
   * GLSL says it in the declaration — `usampler2D` against `sampler2D` — and
   * WGSL says it in the binding type, `texture_2d<u32>` against
   * `texture_2d<f32>`. The generated WGSL header reads this field, and
   * `shaders.test.ts` pins that the GLSL declares `usampler2D` for exactly
   * the `'u32'` specs, so the two halves cannot drift.
   */
  readonly texel?: 'u32' | 'f32';
}

/**
 * The kernel radius of the TERRAIN field, as a multiple of HEX_SIZE
 * (`[presentation.terrain] radius_k`).
 *
 * It is here rather than in one pass, because three consumers must hold the
 * same figure: the terrain pass draws the trench and ridge outlines with it,
 * the territory pass cuts its tint with it so the rock hole edge is the same
 * curve as the ridge body edge, and `../terrain-prep.ts` rasterises the
 * field with it so the EDT's zero line lands on the shader's rim.
 */
export { TERRAIN_RADIUS_K };

/**
 * Where the TERRAIN plane sits in the world, and how coarse it is.
 *
 * `uDistLo` is the world point of its low corner, `uDistUv` turns a world
 * point into its uv, and `uDistStep` is one texel in world px on each axis.
 * One texel in UV is the product of the last two, so it is not a fourth
 * uniform. The terrain pass owns the figures; the territory layer copies
 * them across, so every pass that reads the plane reads it through ONE set
 * of numbers, and ONE spec triple declares them.
 */
export const DIST_PLANE_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uDistLo', type: 'vec2', value: [0, 0], dynamic: true },
  { name: 'uDistUv', type: 'vec2', value: [1 / 5384, 1 / 5356], dynamic: true, note: '1/(texelPx * distSize)' },
  { name: 'uDistStep', type: 'vec2', value: [4, 4], dynamic: true, note: 'one texel in world px' },
];

/** The `uPalette[8]` contract of A.6, flattened for `uniform4fv`. */
export function paletteVec4(): number[] {
  const out: number[] = [0, 0, 0, 0];
  for (const f of FACTIONS) out.push(...rgb(f.color), 1);
  while (out.length < 6 * 4) out.push(0, 0, 0, 0);
  out.push(...rgb(ACCENT_COLOR), 1);
  out.push(...rgb(NEUTRAL_COLOR), 1);
  return out;
}

/**
 * The uniforms the engine's `WorldPass` owns. Every world fragment shader may
 * read them; the ones a shader does not use are optimised out and the setter
 * skips their null location.
 */
export const SHARED_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uOwners', type: 'sampler', value: 0, unit: 0, texel: 'u32', note: 'R8UI cols×rows, faction 0..5' },
  { name: 'uBlocked', type: 'sampler', value: 1, unit: 1, texel: 'u32', note: 'R8UI, 0 sea 1 trench 2 ridge' },
  { name: 'uHeat', type: 'sampler', value: 2, unit: 2, note: 'R8 normalised, ×255 = intensity' },
  // The terrain plane (R432). R and G are the Euclidean distance transform
  // of the two kinds; B and A are the two smoothed FIELDS, which the terrain
  // composite reads in place of summing the kernel at every screen pixel.
  // `RGBA16F` is filterable in core WebGL2, so the plane needs no extension.
  { name: 'uDist', type: 'sampler', value: 3, unit: 3, note: 'RGBA16F terrain plane: distT distR fT fR' },
  // R16F, not R32F. The plane holds one inradius per cell, in world px, and
  // the largest component of the core world measures a few hundred: a half
  // float resolves that to about an eighth of a pixel, which is far below
  // the ring spacing it divides. It is uploaded as FLOAT into an R16F
  // texture, which core WebGL2 allows, and it is read with `texelFetch`, so
  // it is never filtered. `engine.ts` creates it; do not change one side
  // alone (R431 reconciliation).
  { name: 'uCompR', type: 'sampler', value: 4, unit: 4, note: 'R16F cols×rows component inradius' },
  // Unit 9: the survey of the viewer's faction (v2 §4.1, R2626). Only the
  // survey pass reads it; it is a plane of its own, because the hatch
  // reads it on every pixel of the map.
  { name: 'uSurvey', type: 'sampler', value: 9, unit: 9, texel: 'u32', note: 'R8UI, 1 on a cell the survey holds' },
  // Unit 10: the depth band of each cell (v2 §3.2, R2713). Only the depth
  // pass reads it, up to seven times a pixel for the feather.
  { name: 'uDepth', type: 'sampler', value: 10, unit: 10, texel: 'u32', note: 'R8UI, the band 0..4 of a cell' },
  // Unit 11: the speed field of the current looks (R2827). It is the one
  // plane that is not one texel a cell: one texel a node of the flow grid
  // of `chart/pieces/current-field.ts`, `sample_px` world px apart. RG16F
  // and LINEAR, because the speed field pass reads it with a cubic
  // B-spline in four bilinear taps; R16F/RG16F filter in core WebGL2 and
  // core WebGPU alike.
  { name: 'uSpeed', type: 'sampler', value: 11, unit: 11, note: 'RG16F nodes: the speed share u at tide 1, the wet mask' },
  { name: 'uPalette', type: 'vec4[]', value: paletteVec4(), note: '8 entries, shaders read .rgb' },
  { name: 'uGrid', type: 'ivec2', value: [133, 153], dynamic: true },
  { name: 'uHexSize', type: 'float', value: HEX_SIZE },
  { name: 'uColPitch', type: 'float', value: COL_PITCH },
  { name: 'uWorld', type: 'vec2', value: [133 * COL_PITCH, 0], dynamic: true },
  { name: 'uViewInv', type: 'mat3', value: [1, 0, 0, 0, 1, 0, 0, 0, 1], dynamic: true },
  { name: 'uTime', type: 'float', value: 0, dynamic: true },
  { name: 'uZoom', type: 'float', value: 1, dynamic: true },
  { name: 'uDpr', type: 'float', value: 1, dynamic: true },
  { name: 'uPxPerWorld', type: 'float', value: 1, dynamic: true },
];

/**
 * The version line, the precision pragmas and the A.6 uniform contract.
 *
 * It stops before the fragment OUTPUT, which `PRELUDE` states.
 */
const PRELUDE_DECL = `#version 300 es
precision highp float;
precision highp int;
precision highp usampler2D;
precision highp sampler2D;

// ---- the A.6 uniform / sampler contract -------------------------------
uniform usampler2D uOwners;
uniform usampler2D uBlocked;
uniform sampler2D  uHeat;
uniform sampler2D  uDist;
uniform sampler2D  uCompR;
uniform usampler2D uSurvey;
uniform usampler2D uDepth;
uniform sampler2D  uSpeed;
uniform vec4  uPalette[8];
uniform ivec2 uGrid;
uniform float uHexSize;
uniform float uColPitch;
uniform vec2  uWorld;
uniform mat3  uViewInv;
uniform float uTime;
uniform float uZoom;
uniform float uDpr;
uniform float uPxPerWorld;

in vec2 vWorld;
`;

/**
 * The GLSL every pass shares, after its own outputs are declared.
 *
 * `cellAt` is a line-for-line transcription of `world.ts cellRowCol`: the same
 * axial round, the same tie-break, the same odd-r column. `Math.round` is
 * `floor(x + 0.5)`, which is what `jsRound` is for — GLSL's own `round()`
 * takes a half to the nearest even in some implementations, and the two
 * disagree on exactly the cell boundaries. It does NOT clamp to the grid:
 * a cell outside the grid is read as open and unowned at its virtual
 * centre, so a border is stroked along the world edge the way the reference
 * traces it (`neighborAt` returns −1 there).
 */
export const PRELUDE_BODY = `
${IDENTITY_GLSL}const float SQ3 = 1.7320508075688772;

// world.ts:116-119 HEX_OFFSETS — even rows then odd rows. Direction k faces
// the edge between corners k and k+1.
//
// The two parities are two arrays and not one of twelve, because the index
// then holds only the loop counter. Every walk over the six directions is a
// constant-bounded loop, so the compiler unrolls it and each read becomes a
// constant; one array of twelve would carry the row parity in the index and
// the whole table would leave the register file as an indexable temporary
// (R431, the GPU work).
const ivec2 HEX_EVEN[6] = ivec2[6](
  ivec2( 1,  0), ivec2( 0,  1), ivec2(-1,  1), ivec2(-1,  0), ivec2(-1, -1), ivec2( 0, -1));
const ivec2 HEX_ODD[6] = ivec2[6](
  ivec2( 1,  0), ivec2( 1,  1), ivec2( 0,  1), ivec2(-1,  0), ivec2( 0, -1), ivec2( 1, -1));

float jsRound(float x) { return floor(x + 0.5); }

ivec2 cellAt(vec2 p) {                       // world.ts cellRowCol, bit-compatible
  float s = uHexSize;
  float px = p.x - 0.5 * SQ3 * s;
  float py = p.y - s;
  float qf = (SQ3 / 3.0 * px - py / 3.0) / s;
  float rf = (2.0 / 3.0 * py) / s;
  float sf = -qf - rf;
  float q = jsRound(qf), r = jsRound(rf), z = jsRound(sf);
  float dq = abs(q - qf), dr = abs(r - rf), dz = abs(z - sf);
  if (dq > dr && dq > dz) q = -r - z;
  else if (dr > dz) r = -q - z;
  int row = int(r);
  int col = int(q) + ((row - (row & 1)) >> 1);
  return ivec2(col, row);                    // may lie outside the grid
}

vec2 cellCenter(ivec2 c) {                   // world.ts:128-131
  return vec2(SQ3 * uHexSize * (float(c.x) + 0.5 * float(c.y & 1) + 0.5),
              1.5 * uHexSize * float(c.y) + uHexSize);
}

ivec2 neighbor(ivec2 c, int k) { return c + ((c.y & 1) == 1 ? HEX_ODD[k] : HEX_EVEN[k]); }

bool inGrid(ivec2 c) { return c.x >= 0 && c.y >= 0 && c.x < uGrid.x && c.y < uGrid.y; }

vec2 hexCorner(int k) {                      // world.ts:133-136
  float a = radians(60.0 * float(k) - 30.0);
  return uHexSize * vec2(cos(a), sin(a));
}

// A cell outside the grid is open and unowned. That is what makes the world
// edge a border the shader strokes.
int ownerOf(ivec2 c) { return inGrid(c) ? int(texelFetch(uOwners,  c, 0).r) : 0; }
int kindOf (ivec2 c) { return inGrid(c) ? int(texelFetch(uBlocked, c, 0).r) : 0; }

// K(d) = (1 - d²/R²)² for d < R. C¹ at R, so the summed field has no seam
// where a cell drops out of the ring. dk is the analytic gradient.
void kern(vec2 p, vec2 c, float invR2, out float k, out vec2 dk) {
  vec2 v = p - c;
  float u = max(0.0, 1.0 - dot(v, v) * invR2);
  k = u * u;
  dk = (-4.0 * u * invR2) * v;
}

// Box-filter coverage of a stroke of half-width hw at unsigned distance d.
// px is one device pixel measured in world px.
float lineCov(float d, float hw, float px) {
  return clamp((min(d + 0.5 * px, hw) - max(d - 0.5 * px, -hw)) / px, 0.0, 1.0);
}

// Coverage of the region a signed distance calls inside (sd > 0 is inside),
// box-filtered over one device pixel.
float fillCov(float sd, float px) { return clamp(sd / px + 0.5, 0.0, 1.0); }

// Premultiplied source-over.
vec4 over(vec4 dst, vec3 c, float a) { return vec4(c * a, a) + dst * (1.0 - a); }

// One device pixel in world px.
float worldPx() { return 1.0 / max(uZoom * uDpr, 1e-6); }
`;

/**
 * The prelude of a MAP pass: the contract, one premultiplied output, and
 * the shared body. `geo.ts`, `terrain.ts` and `heatmap.ts` take it.
 */
export const PRELUDE = `${PRELUDE_DECL}out vec4 fragColor;          // PREMULTIPLIED sRGB
${PRELUDE_BODY}`;
